import { useDateFormat } from "@/hooks/useDateFormat";
import {
  useWorkplaceRequestCommentMutations,
  useWorkplaceRequestComments
} from "@/hooks/useWorkplaceRequestComments";
import { useWorkplaceRequestEvents } from "@/hooks/useWorkplaceRequestEvents";
import { useWorkplaceRequestMutations } from "@/hooks/useWorkplaceRequestMutations";
import { useWorkplaceRequestTypes } from "@/hooks/useWorkplaceRequestTypes";
import { useWorkplaceRequestById } from "@/hooks/useWorkplaceRequestById";
import { Dialog, DialogContent, DialogTitle } from "@cmp-src/primitives/Dialog";
import { useAuthenticated } from "@gettactic/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { DeleteRequestConfirmationModal } from "../DeleteRequestConfirmationModal";
import { MobileDeleteRequestConfirmationModal } from "../MobileDeleteRequestConfirmationModal";
import { RequestEventsList } from "../RequestEventsList";
import { CommentsSection } from "../RequestDetailsModal/CommentsSection";
import { DeleteCommentModal } from "../RequestDetailsModal/DeleteCommentModal";
import { MobileDeleteCommentModal } from "../RequestDetailsModal/MobileDeleteCommentModal";
import { DescriptionSection } from "../RequestDetailsModal/DescriptionSection";
import { Sidebar } from "../RequestDetailsModal/Sidebar";
import { RequestStatus } from "../../workplace-requests-settings/types";
import { capitalize } from "@gettactic/helpers";
import { RequestDetailsPageProps } from "./types";
import { RequestDetailsSkeleton } from "./RequestDetailsSkeleton";
import { WorkplaceRequestElement } from "@gettactic/api";
import { useIsMobile } from "@v2/hooks/use-mobile";
import { ScrollArea } from "@v2/components/ui/scroll-area";

// Type guard to check if the request is a valid WorkplaceRequestElement
function isValidWorkplaceRequest(
  request: any
): request is WorkplaceRequestElement {
  return (
    request &&
    typeof request.id === "string" &&
    typeof request.title === "string" &&
    typeof request.status === "string" &&
    request.type &&
    typeof request.type.id === "string"
  );
}

export function RequestDetailsPage({
  dontTouchUrl = false,
  requestId,
  isAdmin = false,
  onClose,
  onError,
  isPageMode = false,
  initialRequest = null,
  onRequestUpdate,
  isInMobileDrawer = false,
  schedule = null
}: RequestDetailsPageProps) {
  const queryClient = useQueryClient();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const formatDate = useDateFormat();
  const isMobile = useIsMobile();
  const [newComment, setNewComment] = useState("");
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteCommentConfirm, setShowDeleteCommentConfirm] =
    useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [pendingChanges, setPendingChanges] = useState({
    status: "",
    owner: "",
    type: ""
  });

  // Use the hook to fetch request data when in page mode or when no initial request is provided
  const {
    data: fetchedRequestData,
    isLoading: isRequestLoading,
    error: requestError
  } = useWorkplaceRequestById(requestId || "");

  // Handle the case where API returns an array instead of a single element
  const fetchedRequest = Array.isArray(fetchedRequestData)
    ? fetchedRequestData[0]
    : fetchedRequestData;

  // Use initial request if provided (modal mode), otherwise use fetched request (page mode)
  const request =
    initialRequest ||
    (isValidWorkplaceRequest(fetchedRequest) ? fetchedRequest : null);

  const { allElements: comments, isFetching: commentsLoading } =
    useWorkplaceRequestComments(request?.id || "", { limit: 100 });

  const { createComment, updateComment, deleteComment } =
    useWorkplaceRequestCommentMutations();

  const {
    updateWorkplaceRequestStatus,
    updateWorkplaceRequestOwner,
    deleteWorkplaceRequest,
    updateWorkplaceRequest,
    updateWorkplaceRequestMutation
  } = useWorkplaceRequestMutations();

  const { data: eventsData, isFetching: eventsLoading } =
    useWorkplaceRequestEvents(request?.id || "", { limit: 50 });

  const { allElements: workplaceRequestTypes } = useWorkplaceRequestTypes();

  // Handle request error
  useEffect(() => {
    if (requestError && isPageMode) {
      onError?.("Request not found or unavailable");
    }
  }, [requestError, isPageMode, onError]);

  useEffect(() => {
    if (
      !dontTouchUrl &&
      request &&
      window.location.pathname.split("/").pop() !== String(request?.display_id)
    ) {
      if (!isPageMode) {
        // add a /:display_id to the url so user can copy it to share
        // later details page can be opened from the link
        window.history.pushState(
          null,
          "",
          `/workplace-requests/${request?.display_id}`
        );
      }
    }
  }, [request, isPageMode]);

  useEffect(() => {
    if (request) {
      setEditedDescription(request.description || "");
      setPendingChanges({
        status: capitalize(request.status),
        owner: request.owner_user?.id || "",
        type: request.type.id
      });
    }
  }, [request]);

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !request) return;

    try {
      await createComment.mutateAsync({
        ticketId: request.id,
        params: { comment: newComment }
      });
      setNewComment("");
    } catch (error) {
      console.error("Error creating comment:", error);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!request || !editingCommentText.trim()) return;

    try {
      await updateComment.mutateAsync({
        ticketId: request.id,
        commentId,
        params: { comment: editingCommentText }
      });
      setEditingCommentId(null);
      setEditingCommentText("");
    } catch (error) {
      console.error("Error updating comment:", error);
    }
  };

  const handleDeleteComment = async () => {
    if (!request || !commentToDelete) return;

    try {
      await deleteComment.mutateAsync({
        ticketId: request.id,
        commentId: commentToDelete
      });
      setShowDeleteCommentConfirm(false);
      setCommentToDelete(null);
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const handleStartEditComment = (comment: any) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.comment);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
  };

  const handleConfirmDeleteComment = (commentId: string) => {
    setCommentToDelete(commentId);
    setShowDeleteCommentConfirm(true);
  };

  const handleStatusChange = async (
    requestId: string,
    newStatus: RequestStatus,
    comment?: string
  ) => {
    if (!request) return;

    setPendingChanges((prev) => ({ ...prev, status: newStatus }));

    try {
      await updateWorkplaceRequestStatus(requestId, newStatus, comment);

      const updatedRequest = {
        ...request,
        status: newStatus.toLowerCase() as any
      };

      onRequestUpdate?.(updatedRequest);

      queryClient.invalidateQueries(["workplaceRequestEvents", requestId]);
    } catch (error) {
      console.error("Error updating status:", error);

      setPendingChanges((prev) => ({ ...prev, status: request.status }));
    }
  };

  const handleOwnerChange = async (ownerId: string) => {
    if (!request) return;

    setPendingChanges((prev) => ({ ...prev, owner: ownerId }));

    try {
      await updateWorkplaceRequestOwner(
        request.id,
        ownerId === "no-owner" ? null : ownerId
      );

      const selectedType = workplaceRequestTypes.find(
        (t) => t.id === request?.type.id
      );
      const assignedUsers = selectedType?.assigned_users || [];
      const newOwner =
        ownerId === "no-owner"
          ? undefined
          : assignedUsers.find((user) => user.id === ownerId);

      const updatedRequest = {
        ...request,
        owner_user: newOwner || null
      };

      onRequestUpdate?.(updatedRequest);
    } catch (error) {
      console.error("Error updating owner:", error);

      setPendingChanges((prev) => ({
        ...prev,
        owner: request.owner_user?.id || ""
      }));
    }
  };

  const handleTypeChange = async (typeId: string) => {
    if (!request) return;

    setPendingChanges((prev) => ({ ...prev, type: typeId }));

    try {
      await updateWorkplaceRequest(request.id, {
        type_id: typeId
      });

      const selectedType = workplaceRequestTypes.find((t) => t.id === typeId);
      const updatedRequest = {
        ...request,
        type: {
          id: typeId,
          name: selectedType?.name || "",
          description: selectedType?.description || "",
          llm_context: selectedType?.llm_context || "",
          in_use: selectedType?.in_use || false
        }
      };

      onRequestUpdate?.(updatedRequest);
    } catch (error) {
      console.error("Error updating request type:", error);

      setPendingChanges((prev) => ({ ...prev, type: request.type.id }));
    }
  };

  const handleSaveDescription = async () => {
    if (!request) return;

    try {
      await updateWorkplaceRequest(request.id, {
        description: editedDescription
      });

      const updatedRequest = {
        ...request,
        description: editedDescription
      };

      onRequestUpdate?.(updatedRequest);

      setIsEditingDescription(false);
    } catch (error) {
      console.error("Error updating description:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditedDescription(request?.description || "");
    setIsEditingDescription(false);
  };

  const handleStartEdit = () => {
    setIsEditingDescription(true);
  };

  const handleDeleteRequest = async () => {
    if (!request) return;

    try {
      await deleteWorkplaceRequest(request.id);
      setShowDeleteConfirm(false);

      setTimeout(() => {
        onClose?.();
      }, 100);
    } catch (error) {
      console.error("Error deleting request:", error);
    }
  };

  // Show skeleton when loading in page mode
  if (isPageMode && (isRequestLoading || !request)) {
    return <RequestDetailsSkeleton />;
  }

  // Don't render anything if no request and not in page mode (modal mode)
  if (!request && !isPageMode) return null;

  // If we have a request but it's not valid, show error in page mode
  if (isPageMode && !isValidWorkplaceRequest(request)) {
    onError?.("Invalid request data");
    return <RequestDetailsSkeleton />;
  }

  // At this point, we know request is valid if we're rendering
  const validRequest = request as WorkplaceRequestElement;
  const TitleTag = isPageMode ? H1 : DialogTitle;
  const content = (
    <div className="flex h-full max-sm:flex-col">
      {/* Main Content */}
      <div className="flex-1 p-12 pb-8 max-sm:p-4" data-modal-content>
        {/* Sticky Title with Close Button for Mobile Drawer */}
        <div className="sticky top-0 z-10 bg-white pb-4">
          <div className="flex items-start justify-between">
            <TitleTag className="mb-2 flex-1 pr-4 font-mona-sans font-semibold text-2xl text-gray-900">
              {validRequest.title}
            </TitleTag>
            {isInMobileDrawer && (
              <button
                onClick={onClose}
                className="ml-4 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Description Section */}
        <DescriptionSection
          request={validRequest}
          isAdmin={isAdmin}
          isEditing={isEditingDescription}
          editedDescription={editedDescription}
          onDescriptionChange={setEditedDescription}
          onStartEdit={handleStartEdit}
          onSave={handleSaveDescription}
          onCancel={handleCancelEdit}
          isLoading={updateWorkplaceRequestMutation.isLoading}
        />

        {/* Comments Section */}
        <CommentsSection
          comments={comments}
          commentsLoading={commentsLoading}
          authenticatedUser={authenticatedUser}
          editingCommentId={editingCommentId}
          editingCommentText={editingCommentText}
          onStartEditComment={handleStartEditComment}
          onCancelEditComment={handleCancelEditComment}
          onEditComment={handleEditComment}
          onDeleteComment={handleConfirmDeleteComment}
          updateCommentLoading={updateComment.isLoading}
          newComment={newComment}
          onNewCommentChange={setNewComment}
          onSubmitComment={handleSubmitComment}
          createCommentLoading={createComment.isLoading}
          onEditingCommentTextChange={setEditingCommentText}
        />

        {/* Events Section */}
        <div className="mt-8 border-gray-200 border-t pt-2">
          <h3 className="mb-2 font-medium text-gray-900 text-md">
            Activity History
          </h3>
          <RequestEventsList
            events={
              eventsData?.pages?.flatMap(
                (page) => (page as any)?.elements || []
              ) || []
            }
            users={
              eventsData?.pages?.reduce(
                (acc, page) => {
                  if (page?.users) {
                    return Object.assign(acc, page.users);
                  }
                  return acc;
                },
                {} as Record<string, any>
              ) || {}
            }
            isLoading={eventsLoading}
          />
        </div>
      </div>

      {/* Sidebar */}
      <Sidebar
        request={validRequest}
        isAdmin={isAdmin}
        authenticatedUser={authenticatedUser}
        workplaceRequestTypes={workplaceRequestTypes}
        pendingChanges={pendingChanges}
        onOwnerChange={handleOwnerChange}
        onStatusChange={handleStatusChange}
        onTypeChange={handleTypeChange}
        onDeleteRequest={() => setShowDeleteConfirm(true)}
        formatDate={formatDate}
        schedule={schedule}
      />
    </div>
  );


  // if (isMobile) {
  //   return (
  //     <>
  //       {content}
  //     </>
  //   );
  // }

  // If in mobile drawer, render content directly without modal wrapper
  if (isInMobileDrawer) {
    return (
      <>
        {content}
        
        {/* Mobile Delete Confirmation Modal */}
        <MobileDeleteRequestConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteRequest}
          requestTitle={validRequest.title}
        />

        {/* Mobile Delete Comment Confirmation Modal */}
        <MobileDeleteCommentModal
          isOpen={showDeleteCommentConfirm}
          onClose={() => setShowDeleteCommentConfirm(false)}
          onConfirm={handleDeleteComment}
          isLoading={deleteComment.isLoading}
        />
      </>
    );
  }

  if (isPageMode) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
        {/* Modal-like container */}
        <div className="relative h-[98vh] w-full max-w-7xl overflow-hidden rounded-lg bg-white shadow-xl sm:h-[95vh]">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Page content with unified scroll */}
          <ScrollArea className="h-full">
            {content}
          </ScrollArea>

          {/* Custom Delete Confirmation Modal with higher z-index */}
          <DeleteRequestConfirmationModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDeleteRequest}
            requestTitle={validRequest.title}
          />

          {/* Delete Comment Confirmation Modal */}
          <DeleteCommentModal
            isOpen={showDeleteCommentConfirm}
            onClose={() => setShowDeleteCommentConfirm(false)}
            onConfirm={handleDeleteComment}
            isLoading={deleteComment.isLoading}
          />
        </div>
      </div>
    );
  }


  return (
    <>
      <Dialog
        open={true}
        onOpenChange={() => {
          if (!isPageMode && !dontTouchUrl) {
            window.history.pushState(null, "", "/workplace-requests");
          }
          onClose?.();
        }}
      >
        <DialogContent className="max-h-[95vh] max-w-[calc(100vw-6rem)] gap-0 bg-white p-0">
          {content}
        </DialogContent>
      </Dialog>

      {/* Custom Delete Confirmation Modal with higher z-index */}
      <DeleteRequestConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteRequest}
        requestTitle={validRequest.title}
      />

      {/* Delete Comment Confirmation Modal */}
      <DeleteCommentModal
        isOpen={showDeleteCommentConfirm}
        onClose={() => setShowDeleteCommentConfirm(false)}
        onConfirm={handleDeleteComment}
        isLoading={deleteComment.isLoading}
      />
    </>
  );
}

const H1 = ({ children }: { children: React.ReactNode }) => (
  <h1 className="font-mona-sans font-semibold text-2xl text-gray-900">
    {children}
  </h1>
);
